import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { StatusBadge } from '../components/StatusBadge';
import { useAuth } from '../contexts/AuthContext';
import { ArrowLeft, Loader2, Check, X, MessageSquare, Edit, ChevronLeft, ChevronRight, FileText, History, Layers, User } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { reloadPortalStartupMaps } from '../lib/portalApi';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];

const DetailRow = ({ label, value }) => (
  <div className="py-3 border-b border-gray-100 last:border-0">
    <dt className="text-sm text-muted-foreground mb-1">{label}</dt>
    <dd className="font-medium break-words">{value ?? '—'}</dd>
  </div>
);

// History actions, as shown in Version History
const ACTION_LABELS = { create: 'Submitted', update: 'Edited', resubmit: 'Resubmitted', approve: 'Approved', reject: 'Rejected' };
const ACTION_STYLES = {
  create: 'bg-blue-100 text-blue-800',
  update: 'bg-amber-100 text-amber-800',
  resubmit: 'bg-amber-100 text-amber-800',
  approve: 'bg-green-100 text-green-800',
  reject: 'bg-red-100 text-red-800',
};

const formatDate = (dateString) =>
  dateString
    ? new Date(dateString).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' })
    : '—';

export default function CategoryConversionDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const backUrl = searchParams.get('from') === 'pending' ? '/pending-approvals' : '/category-conversions';
  const isExisting = searchParams.get('source') === 'existing';

  const [conversion, setConversion] = useState(null);
  const [loading, setLoading] = useState(true);
  const [approvalDialog, setApprovalDialog] = useState({ open: false, action: null });
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Emission factors this conversion applies to (works for pending and existing conversions too)
  const [efs, setEfs] = useState({ items: [], total: 0, total_pages: 0 });
  const [efPage, setEfPage] = useState(1);
  const [efPageSize, setEfPageSize] = useState(10);
  const [efLoading, setEfLoading] = useState(true);

  // Version history and comments: one entry per version (existing conversions have none: they were added directly)
  const [logs, setLogs] = useState([]);
  const { user, canApprove } = useAuth();

  const fetchConversion = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/category-conversions/${id}`, { params: isExisting ? { source: 'existing' } : {} });
      setConversion(response.data);
    } catch (error) {
      toast.error('Failed to load category conversion');
    } finally {
      setLoading(false);
    }
  }, [id, isExisting]);

  useEffect(() => {
    fetchConversion();
  }, [fetchConversion]);

  useEffect(() => {
    setEfLoading(true);
    axios.get(`${API_URL}/category-conversions/${id}/emission-factors`, {
      params: { page: efPage, page_size: efPageSize, ...(isExisting && { source: 'existing' }) },
    })
      .then(res => setEfs(res.data))
      .catch(() => toast.error('Failed to load applicable emission factors'))
      .finally(() => setEfLoading(false));
  }, [id, isExisting, efPage, efPageSize]);

  useEffect(() => {
    if (isExisting) return;
    axios.get(`${API_URL}/category-conversions/${id}/history`)
      .then(res => setLogs(res.data.history))
      .catch(() => { /* history tabs just stay empty */ });
  }, [id, isExisting, conversion?.status, conversion?.version]);

  const closeDialog = () => {
    setApprovalDialog({ open: false, action: null });
    setComment('');
  };

  const handleApproval = async () => {
    const { action } = approvalDialog;
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/category-conversions/${id}/approve`, {
        action: approvalDialog.action,
        comment: comment || null,
      });
      toast.success(`Category conversion ${action === 'approve' ? 'approved' : 'rejected'}`);
      if (action === 'approve') reloadPortalStartupMaps();
      closeDialog();
      if (backUrl === '/pending-approvals') navigate(backUrl);
      else fetchConversion();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to process approval');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center py-24">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </DashboardLayout>
    );
  }

  if (!conversion) {
    return (
      <DashboardLayout>
        <div className="text-center py-24">
          <p className="text-muted-foreground">Category conversion not found</p>
          <Button className="mt-4" onClick={() => navigate('/category-conversions')}>Back to Category Conversions</Button>
        </div>
      </DashboardLayout>
    );
  }

  const comments = logs.filter(log => log.comment);

  // Pending / rejected submissions can be edited by whoever created them, or by an admin
  const canEditConversion =
    ['pending', 'rejected'].includes(conversion.status) &&
    (conversion.user_email === user?.email || user?.role === 'admin');

  return (
    <DashboardLayout>
      <div className="space-y-6 animate-fade-in" data-testid="category-conversion-detail-page">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate(backUrl)} data-testid="back-btn">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <div className="flex items-center gap-3">
                <h1 className="font-heading text-3xl font-bold text-foreground">
                  Category Conversion {isExisting ? `#${conversion.id}` : (conversion.category_conversion_id ? `#${conversion.category_conversion_id}` : '(not approved yet)')}
                </h1>
                <StatusBadge status={conversion.status} />
              </div>
              <p className="text-muted-foreground mt-1">
                {conversion.conversion_from} → {conversion.conversion_to} (×{conversion.value})
              </p>
            </div>
          </div>

          <div className="flex gap-2">
            {canEditConversion && (
              <Link to={`/category-conversions/${conversion.id}/edit`}>
                <Button variant="outline" data-testid="edit-btn">
                  <Edit className="w-4 h-4 mr-2" />
                  {conversion.status === 'rejected' ? 'Edit & Resubmit' : 'Edit'}
                </Button>
              </Link>
            )}
            {canApprove() && conversion.status === 'pending' && (
              <>
              <Button
                variant="outline"
                className="text-red-600 border-red-200 hover:bg-red-50"
                onClick={() => setApprovalDialog({ open: true, action: 'reject' })}
                data-testid="reject-btn"
              >
                <X className="w-4 h-4 mr-2" />
                Reject
              </Button>
              <Button
                className="bg-green-600 hover:bg-green-700"
                onClick={() => setApprovalDialog({ open: true, action: 'approve' })}
                data-testid="approve-btn"
              >
                <Check className="w-4 h-4 mr-2" />
                Approve
              </Button>
              </>
            )}
          </div>
        </div>

        {conversion.status === 'rejected' && conversion.comment && (
          <Card className="border-red-200 bg-red-50">
            <CardContent className="p-4 flex items-start gap-3">
              <MessageSquare className="w-5 h-5 text-red-600 mt-0.5" />
              <div>
                <h3 className="font-semibold text-red-800 mb-1">Rejection Reason</h3>
                <p className="text-red-700">{conversion.comment}</p>
              </div>
            </CardContent>
          </Card>
        )}

        <Tabs defaultValue="details" className="space-y-6">
          <TabsList>
            <TabsTrigger value="details" className="gap-2" data-testid="tab-details">
              <FileText className="w-4 h-4" />
              Details
            </TabsTrigger>
            <TabsTrigger value="history" className="gap-2" data-testid="tab-history">
              <History className="w-4 h-4" />
              Version History ({logs.length})
            </TabsTrigger>
            <TabsTrigger value="comments" className="gap-2" data-testid="tab-comments">
              <MessageSquare className="w-4 h-4" />
              Comments ({comments.length})
            </TabsTrigger>
            <TabsTrigger value="applicable-efs" className="gap-2" data-testid="tab-applicable-efs">
              <Layers className="w-4 h-4" />
              Applicable EFs ({efs.total})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="details">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card className="border-gray-200">
            <CardHeader><CardTitle className="font-heading text-lg">Conversion</CardTitle></CardHeader>
            <CardContent>
              <DetailRow label="Conversion Sub Category" value={conversion.conversion_sub_category} />
              <DetailRow label="Conversion Type" value={conversion.conversion_type} />
              <DetailRow label="Conversion Sub Type" value={conversion.conversion_sub_type} />
              <DetailRow label="Conversion From" value={conversion.conversion_from} />
              <DetailRow label="Conversion To" value={conversion.conversion_to} />
              <DetailRow label="Value" value={conversion.value} />
              <DetailRow
                label="Emission Factor"
                value={conversion.emission_factor_id ? <Link to={`/emission-factors/${conversion.emission_factor_id}`} className="text-primary hover:underline">#{conversion.emission_factor_id}</Link> : null}
              />
            </CardContent>
          </Card>

          <Card className="border-gray-200">
            <CardHeader><CardTitle className="font-heading text-lg">Source & Submission</CardTitle></CardHeader>
            <CardContent>
              <DetailRow label="Source Name" value={conversion.source_name} />
              <DetailRow label="Source URL" value={conversion.source_url} />
              <DetailRow label="Additional Information" value={conversion.additional_information} />
              <DetailRow label="Status" value={<StatusBadge status={conversion.status} />} />
              <DetailRow label="Created By" value={conversion.user_email} />
              <DetailRow label="Created At" value={formatDate(conversion.created_at)} />
            </CardContent>
          </Card>
        </div>

          </TabsContent>

          <TabsContent value="history">
            <Card className="border-gray-200">
              <CardHeader><CardTitle className="font-heading text-lg">Version History</CardTitle></CardHeader>
              <CardContent>
                {logs.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <History className="w-12 h-12 mx-auto mb-3 opacity-30" />
                    <p>{isExisting ? 'This conversion was added directly in the database, so it has no Atlas history' : 'No history yet'}</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {logs.map((log) => {
                      const values = ['create', 'update', 'resubmit'].includes(log.action) ? log.data : null;
                      return (
                        <div key={log.version} className="p-4 border rounded-lg" data-testid={`history-entry-${log.version}`}>
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <span className="text-xs font-mono text-muted-foreground">v{log.version}</span>
                              <span className={`px-2 py-1 rounded text-xs font-medium ${ACTION_STYLES[log.action] || 'bg-gray-100 text-gray-800'}`}>
                                {ACTION_LABELS[log.action] || log.action}
                              </span>
                            </div>
                            <span className="text-sm text-muted-foreground">{formatDate(log.created_at)}</span>
                          </div>
                          {values && (
                            <p className="text-sm mb-2">
                              {values.conversion_sub_category} / {values.conversion_type} / {values.conversion_sub_type}
                              {' · '}1 {values.conversion_from} = {values.value} {values.conversion_to}
                              {values.source_name && ` · ${values.source_name}`}
                            </p>
                          )}
                          {log.comment && <p className="text-sm mb-2">"{log.comment}"</p>}
                          <div className="flex items-center gap-2 text-sm text-muted-foreground">
                            <User className="w-4 h-4" />
                            <span className="font-medium">{log.user_name}</span>
                            <span>({log.user_email})</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="comments">
            <Card className="border-gray-200">
              <CardHeader><CardTitle className="font-heading text-lg">Comments</CardTitle></CardHeader>
              <CardContent>
                {comments.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <MessageSquare className="w-12 h-12 mx-auto mb-3 opacity-30" />
                    <p>No comments yet</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {comments.map((log) => (
                      <div
                        key={log.version}
                        className={`p-4 border rounded-lg ${log.action === 'reject' ? 'border-red-200 bg-red-50' : 'border-green-200 bg-green-50'}`}
                        data-testid={`comment-entry-${log.version}`}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <span className={`px-2 py-1 rounded text-xs font-medium ${ACTION_STYLES[log.action]}`}>
                            {log.action === 'reject' ? 'Rejection Feedback' : 'Approval Note'}
                          </span>
                          <span className="text-sm text-muted-foreground">{formatDate(log.created_at)}</span>
                        </div>
                        <p className="text-sm mb-2">{log.comment}</p>
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <User className="w-4 h-4" />
                          <span className="font-medium">{log.user_name}</span>
                          <span>({log.user_email})</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="applicable-efs">
        <Card className="border-gray-200" data-testid="applicable-efs-card">
          <CardHeader>
            <CardTitle className="font-heading text-lg">Applicable Emission Factors</CardTitle>
            <p className="text-sm text-muted-foreground">
              Emission factors matching this conversion's sub category, type and sub type. A "*" matches every value, so one conversion can apply to many emission factors.
            </p>
          </CardHeader>
          <CardContent className="p-0">
            {efLoading ? (
              <div className="flex items-center justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
            ) : efs.total === 0 ? (
              <p className="text-center text-muted-foreground py-10">No emission factors match this conversion</p>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-gray-50">
                        <TableHead className="table-dense">EF ID</TableHead>
                        <TableHead className="table-dense">Category</TableHead>
                        <TableHead className="table-dense">Sub Category</TableHead>
                        <TableHead className="table-dense">Type</TableHead>
                        <TableHead className="table-dense">Sub Type</TableHead>
                        <TableHead className="table-dense">Country</TableHead>
                        <TableHead className="table-dense">Input Unit</TableHead>
                        <TableHead className="table-dense">Unit Category</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {efs.items.map((ef) => (
                        <TableRow key={ef.emission_factor_id} className="table-dense">
                          <TableCell className="font-mono text-sm">
                            <Link to={`/emission-factors/${ef.emission_factor_id}`} className="text-primary hover:underline">{ef.emission_factor_id}</Link>
                          </TableCell>
                          <TableCell>{ef.category || '—'}</TableCell>
                          <TableCell>{ef.sub_category || '—'}</TableCell>
                          <TableCell>{ef.type || '—'}</TableCell>
                          <TableCell>{ef.sub_type || '—'}</TableCell>
                          <TableCell>{ef.country || '—'}</TableCell>
                          <TableCell>{ef.input_unit || '—'}</TableCell>
                          <TableCell>{ef.unit_category || '—'}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
                <div className="flex flex-col sm:flex-row items-center justify-between gap-3 px-4 py-3 border-t bg-gray-50">
                  <div className="flex items-center gap-4">
                    <span className="text-sm text-muted-foreground">
                      Showing {(efPage - 1) * efPageSize + 1} to {Math.min(efPage * efPageSize, efs.total)} of {efs.total} emission factors
                    </span>
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-muted-foreground">Rows per page:</span>
                      <Select value={String(efPageSize)} onValueChange={(value) => { setEfPageSize(Number(value)); setEfPage(1); }}>
                        <SelectTrigger className="w-[70px] h-8" data-testid="ef-page-size-select"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {PAGE_SIZE_OPTIONS.map(size => <SelectItem key={size} value={String(size)}>{size}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button variant="outline" size="sm" onClick={() => setEfPage(p => Math.max(1, p - 1))} disabled={efPage === 1} data-testid="ef-prev-page-btn">
                      <ChevronLeft className="w-4 h-4 mr-1" />
                      Previous
                    </Button>
                    {/* up to 5 page numbers, centred on the current page */}
                    {Array.from({ length: Math.min(5, efs.total_pages) }, (_, i) => {
                      const first = Math.min(Math.max(1, efPage - 2), Math.max(1, efs.total_pages - 4));
                      const pageNum = first + i;
                      return (
                        <Button
                          key={pageNum}
                          variant={pageNum === efPage ? 'default' : 'outline'}
                          size="sm"
                          className="w-8 h-8 p-0"
                          onClick={() => setEfPage(pageNum)}
                          data-testid={`ef-page-${pageNum}-btn`}
                        >
                          {pageNum}
                        </Button>
                      );
                    })}
                    <Button variant="outline" size="sm" onClick={() => setEfPage(p => Math.min(efs.total_pages, p + 1))} disabled={efPage === efs.total_pages} data-testid="ef-next-page-btn">
                      Next
                      <ChevronRight className="w-4 h-4 ml-1" />
                    </Button>
                  </div>
                </div>
              </>
            )}
          </CardContent>
        </Card>
          </TabsContent>
        </Tabs>

        <Dialog open={approvalDialog.open} onOpenChange={(open) => !open && closeDialog()}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{approvalDialog.action === 'approve' ? 'Approve' : 'Reject'} Category Conversion</DialogTitle>
              <DialogDescription>
                {approvalDialog.action === 'approve'
                  ? 'This will add the conversion to the category_conversion table in preprod and production.'
                  : 'Please provide a reason for rejecting this conversion.'}
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-2">
              <Label htmlFor="comment">
                Comment {approvalDialog.action === 'reject' && <span className="text-red-500">*</span>}
              </Label>
              <Textarea
                id="comment"
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder={approvalDialog.action === 'reject' ? 'Please provide reason for rejection' : 'Optional comment'}
                rows={3}
                data-testid="approval-comment"
              />
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={closeDialog}>Cancel</Button>
              <Button
                onClick={handleApproval}
                disabled={submitting || (approvalDialog.action === 'reject' && !comment)}
                className={approvalDialog.action === 'approve' ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'}
                data-testid="confirm-approval-btn"
              >
                {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                {approvalDialog.action === 'approve' ? 'Approve' : 'Reject'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
